import {
  WSButton,
  WSContainer,
  WSFlexBox,
  WSGrid,
  WSLayout,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { Address, CardType } from "@wingspanhq/wingspan-api-sdk";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesCards } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryPrimaryComplianceEntity } from "../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorCardShippingAddress } from "../selectors/selectorCardShippingAddress";
import { AddressText } from "../../../shared/components/AddressText/AddressText";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import { PreviewPhysicalCard } from "../components/PreviewPhysicalCard";
import { WALLET_ROOT_PATH } from "../index";
import { useCardOrderedModal } from "../modals/OrderConfirmation/useCardOrderedModal";
import { useUpdateBillingAddressModal } from "../modals/OrderConfirmation/useUpdateBillingAddress";

export const RouteConfirmOrderPhysicalCard = () => {
  const history = useHistory();
  const queryPrimaryComplianceEntity = useQueryPrimaryComplianceEntity();
  const [createCard, createCardMeta] = queriesCards.createCard.useMutation();
  const updateBillingAddressModal = useUpdateBillingAddressModal();
  const cardOrderedModal = useCardOrderedModal();
  const [shippingAddress, setShippingAddress] = React.useState<
    Address | undefined
  >(undefined);

  const [error, setError] = React.useState<string>();

  return (
    <WSLayout
      headerCenter={
        <WSText.ParagraphSm weight="medium">Order card</WSText.ParagraphSm>
      }
      smallHeader={true}
      onClose={() => {
        history.push(WALLET_ROOT_PATH);
      }}
      line
    >
      <WSContainer verticalPadding>
        <WSGrid>
          <WSGrid.Item span={{ m: "4" }} />
          <WSGrid.Item span={{ m: "4" }}>
            <WSQueries queries={{ queryPrimaryComplianceEntity }}>
              {({ queryPrimaryComplianceEntityData: complianceEntity }) => {
                const legalName = complianceEntity?.individualLegalName;
                const address =
                  shippingAddress ??
                  complianceEntity?.physicalAddress ??
                  undefined;

                return (
                  <WSList gap="L">
                    <WSText.Paragraph weight="medium">
                      Order a free Wingspan business debit card
                    </WSText.Paragraph>
                    <PreviewPhysicalCard />
                    <WSText.Paragraph weight="medium">
                      Confirm your information
                    </WSText.Paragraph>
                    <WSText.ParagraphSm color="gray500">
                      Please review the information we have on file. Confirm or
                      update the mailing address where the card will be sent.
                    </WSText.ParagraphSm>
                    <WSPanel colorBackground="white">
                      <WSGrid gutter={{ m: "XL" }}>
                        <WSGrid.Item span={{ m: "6" }}>
                          <FieldView
                            label="Legal first name"
                            value={legalName?.givenName}
                          ></FieldView>
                        </WSGrid.Item>
                        <WSGrid.Item span={{ m: "6" }}>
                          <FieldView
                            label="Legal last name"
                            value={legalName?.familyName}
                          ></FieldView>
                        </WSGrid.Item>
                        <WSGrid.Item span={{ m: "12" }}>
                          <WSFlexBox justify="space-between">
                            <FieldView
                              label="Address"
                              value={
                                <WSText.ParagraphSm
                                  weight="book"
                                  singleLine={false}
                                >
                                  <AddressText
                                    addressLine1={address?.line1}
                                    addressLine2={address?.line2}
                                    city={address?.city}
                                    state={address?.state}
                                    postalCode={address?.postalCode}
                                    country={address?.country}
                                  />
                                </WSText.ParagraphSm>
                              }
                            />
                            <WSButton.Link
                              size="M"
                              onClick={() => {
                                updateBillingAddressModal.open({
                                  address,
                                  onSuccess: newAddress => {
                                    setShippingAddress(newAddress);
                                  }
                                });
                              }}
                            >
                              Edit
                            </WSButton.Link>
                          </WSFlexBox>
                        </WSGrid.Item>
                      </WSGrid>
                    </WSPanel>
                    <ErrorAlert
                      mb="XL"
                      error={error || createCardMeta.error}
                      context="BankCard"
                    />
                    <WSButton.Primary
                      mt="XL"
                      fullWidth
                      size="M"
                      onAsyncClick={async () => {
                        const cardShippingAddress =
                          selectorCardShippingAddress(address);

                        if (cardShippingAddress) {
                          await createCard(
                            {
                              cardCreate: {
                                cardType: CardType.PHYSICAL,
                                shippingAddress: cardShippingAddress
                              }
                            },
                            {
                              onSuccess: async () => {
                                await cardOrderedModal.open();

                                history.push(WALLET_ROOT_PATH);
                              }
                            }
                          );
                        } else {
                          setError(
                            "Invalid address. Please update your address."
                          );
                        }
                      }}
                    >
                      Confirm & Continue
                    </WSButton.Primary>
                  </WSList>
                );
              }}
            </WSQueries>
          </WSGrid.Item>
        </WSGrid>
      </WSContainer>
    </WSLayout>
  );
};
